/**
 * Boolean modifier — pure typed-array core (Three.js-free).
 *
 * Runs a CSG operation (union / difference / intersect) between the owner
 * mesh and a cutter mesh (another SceneObject referenced by name). The cutter
 * is resolved upstream by `resolveModifierEvaluation`, which pre-evaluates
 * the cutter's own modifier stack and transforms its positions into the
 * owner's local space so this core sees the cutter "as it appears in the
 * viewport" (WYSIWYG).
 *
 * Backend: manifold-3d — an exact CSG kernel (the same used by OpenSCAD and
 * Blender's exact-boolean mode). Watertight by construction, robust on
 * coplanar faces and near-degenerate inputs.
 *
 * CSG retopologises the mesh, so derived data (`normals`, `uvs`, skinning,
 * morph arrays) is invalidated. Manifold's per-run `originalID` and
 * per-triangle `faceID` channels are used to rebuild material groups and face
 * provenance, then coplanar adjacent triangles with matching provenance are
 * merged into clean polygon faces where fan triangulation is safe.
 *
 * Vertex provenance maps unchanged owner regions to their nearest input
 * vertex so vertex groups / skin radii / sculpt displacements survive the
 * bake on that side of the op. Skin weights, morph targets, and UVs on the
 * owner are cleared and left as future work.
 *
 * When manifold-3d is not yet ready (WASM still loading), or when the
 * cutter is missing / empty, the core returns the input geometry unchanged
 * — matching the graceful-degradation pattern of Shrinkwrap.
 */

import type { Manifold, Mesh } from "manifold-3d";
import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import {
  faceProvenanceFromSourceMap,
  identityProvenance,
  provenanceFromSourceMap,
} from "../../modeling/provenance.ts";
import {
  buildEdgesAndCornerEdges,
  numEdgeKey,
  topologyFromTriangles,
  triangulateTopology,
} from "../../modeling/topology.ts";
import type {
  FaceAttributeTable,
  PolygonTopology,
  SharedTriangleIndices,
} from "../../modeling/types.ts";
import {
  asSharedTriangleIndices,
  EMPTY_SHARED_TRIANGLE_INDICES,
  normalizeFaceAttributes,
} from "../../modeling/types.ts";
import { getManifold } from "./manifoldRuntime.ts";
import type {
  GeometryGroup,
  ModifierEvaluationInput,
  ModifierGeometry,
} from "./modifierTypes.ts";

type BooleanMod = Extract<BlendModifier, { kind: "boolean" }>;

/**
 * Axis-aligned grid hash for "nearest owner vertex" provenance lookups.
 * Same shape as shrinkwrapCore's nearest-surface grid, specialised for a
 * point query against a sparse vertex cloud.
 */
interface VertexGrid {
  minX: number;
  minY: number;
  minZ: number;
  cellSize: number;
  nx: number;
  ny: number;
  nz: number;
  /** For each cell, a list of owner vertex indices that fall inside it. */
  cells: Int32Array[];
}

function buildVertexGrid(positions: Float32Array): VertexGrid {
  const n = positions.length / 3;
  if (n === 0) {
    return {
      minX: 0,
      minY: 0,
      minZ: 0,
      cellSize: 1,
      nx: 1,
      ny: 1,
      nz: 1,
      cells: [new Int32Array(0)],
    };
  }
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = positions[i * 3],
      y = positions[i * 3 + 1],
      z = positions[i * 3 + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  // Target ~1 vertex per cell on average — sqrt(cuberoot) of vertex count on
  // each axis, clamped so we don't degenerate to a single cell for tiny or
  // very uniform inputs.
  const dx = Math.max(1e-6, maxX - minX);
  const dy = Math.max(1e-6, maxY - minY);
  const dz = Math.max(1e-6, maxZ - minZ);
  const target = Math.max(1, Math.cbrt(n));
  const cellSize = Math.max(dx, dy, dz) / target;
  const nx = Math.max(1, Math.ceil(dx / cellSize));
  const ny = Math.max(1, Math.ceil(dy / cellSize));
  const nz = Math.max(1, Math.ceil(dz / cellSize));
  const cellCount = nx * ny * nz;

  // Two-pass CSR-style fill: count, then scatter.
  const counts = new Uint32Array(cellCount);
  for (let i = 0; i < n; i++) {
    const cx = Math.min(
      nx - 1,
      Math.max(0, Math.floor((positions[i * 3] - minX) / cellSize)),
    );
    const cy = Math.min(
      ny - 1,
      Math.max(0, Math.floor((positions[i * 3 + 1] - minY) / cellSize)),
    );
    const cz = Math.min(
      nz - 1,
      Math.max(0, Math.floor((positions[i * 3 + 2] - minZ) / cellSize)),
    );
    counts[cz * nx * ny + cy * nx + cx]++;
  }
  const cells: Int32Array[] = new Array(cellCount);
  for (let c = 0; c < cellCount; c++) cells[c] = new Int32Array(counts[c]);
  const cursor = new Uint32Array(cellCount);
  for (let i = 0; i < n; i++) {
    const cx = Math.min(
      nx - 1,
      Math.max(0, Math.floor((positions[i * 3] - minX) / cellSize)),
    );
    const cy = Math.min(
      ny - 1,
      Math.max(0, Math.floor((positions[i * 3 + 1] - minY) / cellSize)),
    );
    const cz = Math.min(
      nz - 1,
      Math.max(0, Math.floor((positions[i * 3 + 2] - minZ) / cellSize)),
    );
    const c = cz * nx * ny + cy * nx + cx;
    cells[c][cursor[c]++] = i;
  }
  return { minX, minY, minZ, cellSize, nx, ny, nz, cells };
}

/** Return the nearest owner-vertex index to (px, py, pz), or -1 if none. */
function nearestOwnerVertex(
  grid: VertexGrid,
  positions: Float32Array,
  px: number,
  py: number,
  pz: number,
): number {
  const { minX, minY, minZ, cellSize, nx, ny, nz, cells } = grid;
  const cx = Math.min(nx - 1, Math.max(0, Math.floor((px - minX) / cellSize)));
  const cy = Math.min(ny - 1, Math.max(0, Math.floor((py - minY) / cellSize)));
  const cz = Math.min(nz - 1, Math.max(0, Math.floor((pz - minZ) / cellSize)));

  let bestIdx = -1;
  let bestDistSq = Infinity;
  // Expand the search radius in cell rings until at least one hit is found,
  // then do one extra ring to make sure a diagonal neighbour cell can't
  // contain a closer vertex than our current best.
  const maxRing = Math.max(nx, ny, nz);
  let extraRings = -1;
  for (let ring = 0; ring <= maxRing; ring++) {
    const rz0 = Math.max(0, cz - ring),
      rz1 = Math.min(nz - 1, cz + ring);
    const ry0 = Math.max(0, cy - ring),
      ry1 = Math.min(ny - 1, cy + ring);
    const rx0 = Math.max(0, cx - ring),
      rx1 = Math.min(nx - 1, cx + ring);
    for (let k = rz0; k <= rz1; k++) {
      for (let j = ry0; j <= ry1; j++) {
        for (let i = rx0; i <= rx1; i++) {
          // Only touch the shell of the current ring, not its interior (those
          // cells were already visited in a smaller ring).
          if (
            ring > 0 &&
            i > rx0 &&
            i < rx1 &&
            j > ry0 &&
            j < ry1 &&
            k > rz0 &&
            k < rz1
          ) {
            continue;
          }
          const cell = cells[k * nx * ny + j * nx + i];
          for (let m = 0; m < cell.length; m++) {
            const vi = cell[m];
            const vx = positions[vi * 3];
            const vy = positions[vi * 3 + 1];
            const vz = positions[vi * 3 + 2];
            const ddx = vx - px,
              ddy = vy - py,
              ddz = vz - pz;
            const d2 = ddx * ddx + ddy * ddy + ddz * ddz;
            if (d2 < bestDistSq) {
              bestDistSq = d2;
              bestIdx = vi;
            }
          }
        }
      }
    }
    if (bestIdx !== -1) {
      if (extraRings < 0) extraRings = 1;
      else if (extraRings-- === 0) break;
    }
  }
  return bestIdx;
}

/**
 * Source side associated with a Manifold originalID. The Boolean output is
 * still baked onto the owner object, so cutter-side faces are treated as
 * generated faces for owner face-provenance purposes.
 */
type BooleanSide = "owner" | "cutter";

interface OriginalRunInfo {
  side: BooleanSide;
  materialIndex: number;
}

interface BuiltManifoldMesh {
  mesh: Mesh;
  originalInfo: Map<number, OriginalRunInfo>;
  faceCount: number;
}

interface ResultFaceMeta {
  materialIndex: number;
  ownerFaceIndex: number;
  sourceKey: string;
}

interface PolygonizedBooleanTopology {
  topology: PolygonTopology;
  indices: SharedTriangleIndices;
  sourceFaceForNewFace: Int32Array;
  materialIndex: Uint16Array;
}

function topologyFaceCount(geo: ModifierGeometry): number {
  return geo.topology
    ? geo.topology.offsets.length - 1
    : geo.indices.length / 3;
}

function triangleFaceIDs(geo: ModifierGeometry): Int32Array {
  const triCount = geo.indices.length / 3;
  const ids = new Int32Array(triCount);
  if (!geo.topology) {
    for (let t = 0; t < triCount; t++) ids[t] = t;
    return ids;
  }

  let tri = 0;
  const { offsets } = geo.topology;
  for (let f = 0; f < offsets.length - 1 && tri < triCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    const faceTriCount = size >= 3 ? size - 2 : 0;
    for (let k = 0; k < faceTriCount && tri < triCount; k++) ids[tri++] = f;
  }
  for (; tri < triCount; tri++) ids[tri] = tri;
  return ids;
}

function triangleMaterialIndices(
  geo: ModifierGeometry,
  faceIDs: Int32Array,
): Uint16Array {
  const triCount = geo.indices.length / 3;
  const materials = new Uint16Array(triCount);
  const faceMaterials = geo.faceAttributes?.materialIndex;
  if (faceMaterials) {
    for (let t = 0; t < triCount; t++) {
      const f = faceIDs[t];
      materials[t] = f >= 0 && f < faceMaterials.length ? faceMaterials[f] : 0;
    }
    return materials;
  }

  for (const group of geo.groups) {
    const startTri = Math.max(0, Math.floor(group.start / 3));
    const endTri = Math.min(
      triCount,
      Math.ceil((group.start + group.count) / 3),
    );
    const materialIndex = clampU16(group.materialIndex ?? 0);
    for (let t = startTri; t < endTri; t++) materials[t] = materialIndex;
  }
  return materials;
}

/**
 * Build a `manifold-3d` Mesh from a ModifierGeometry's positions + triangle
 * indices. `numProp = 3` means the only properties stored are x, y, z — no
 * normals, UVs, or other per-vertex attributes crossed into the CSG kernel.
 *
 * The returned Mesh is then `.merge()`-ed to auto-weld near-coincident
 * vertices before constructing the Manifold — this is what turns an
 * indexed triangle soup into a watertight 2-manifold even when the source
 * mesh has tiny numerical seams (a very common case after an upstream
 * mirror or subsurf).
 *
 * Manifold's `runOriginalID` and `faceID` channels are populated so the output
 * can be traced back to owner/cutter side, material run, and source face.
 * The Boolean modifier currently bakes into the owner object without merging
 * cutter material slots, so cutter-side result faces intentionally map to
 * material slot 0 even though their original IDs are still tracked.
 */
function buildManifoldMesh(
  ManifoldCtor: typeof Manifold,
  MeshCtor: typeof Mesh,
  geo: ModifierGeometry,
  side: BooleanSide,
  tolerance: number,
): BuiltManifoldMesh {
  const indices = geo.indices;
  const triCount = indices.length / 3;
  const faceIDs = triangleFaceIDs(geo);
  const triMaterials = triangleMaterialIndices(geo, faceIDs);
  const uniqueMaterials = Array.from(new Set(triMaterials)).sort(
    (a, b) => a - b,
  );
  const firstOriginalID = ManifoldCtor.reserveIDs(
    Math.max(1, uniqueMaterials.length),
  );
  const originalIDByMaterial = new Map<number, number>();
  const originalInfo = new Map<number, OriginalRunInfo>();
  uniqueMaterials.forEach((materialIndex, i) => {
    const originalID = firstOriginalID + i;
    originalIDByMaterial.set(materialIndex, originalID);
    originalInfo.set(originalID, { side, materialIndex });
  });

  const order = new Uint32Array(triCount);
  for (let t = 0; t < triCount; t++) order[t] = t;
  order.sort((a, b) => {
    const idA = originalIDByMaterial.get(triMaterials[a]) ?? firstOriginalID;
    const idB = originalIDByMaterial.get(triMaterials[b]) ?? firstOriginalID;
    return idA === idB ? a - b : idA - idB;
  });

  const triVerts = new Uint32Array(indices.length);
  const faceID = new Uint32Array(triCount);
  const runIndex: number[] = [];
  const runOriginalID: number[] = [];
  let currentOriginalID = -1;
  for (let outTri = 0; outTri < triCount; outTri++) {
    const srcTri = order[outTri];
    const materialIndex = triMaterials[srcTri];
    const originalID =
      originalIDByMaterial.get(materialIndex) ?? firstOriginalID;
    if (originalID !== currentOriginalID) {
      runIndex.push(outTri * 3);
      runOriginalID.push(originalID);
      currentOriginalID = originalID;
    }

    const src = srcTri * 3;
    const dst = outTri * 3;
    triVerts[dst] = indices[src];
    triVerts[dst + 1] = indices[src + 1];
    triVerts[dst + 2] = indices[src + 2];
    faceID[outTri] = Math.max(0, faceIDs[srcTri]);
  }
  runIndex.push(triCount * 3);

  // Manifold's Mesh copies the buffers internally, but constructing new
  // Float32Array/Uint32Array views over our typed arrays is cheap and gives
  // us clean ownership semantics.
  const mesh = new MeshCtor({
    numProp: 3,
    vertProperties: new Float32Array(geo.positions),
    triVerts,
    runIndex: new Uint32Array(runIndex),
    runOriginalID: new Uint32Array(runOriginalID),
    faceID,
    tolerance: Math.max(0, tolerance),
  });
  mesh.merge();
  return { mesh, originalInfo, faceCount: topologyFaceCount(geo) };
}

function combineOriginalInfo(
  ...maps: readonly Map<number, OriginalRunInfo>[]
): Map<number, OriginalRunInfo> {
  const out = new Map<number, OriginalRunInfo>();
  for (const map of maps) {
    for (const [id, info] of map) out.set(id, info);
  }
  return out;
}

function resultTriangleMetadata(
  resultMesh: Mesh,
  originalInfo: ReadonlyMap<number, OriginalRunInfo>,
  ownerFaceCount: number,
): ResultFaceMeta[] {
  const triCount = resultMesh.triVerts.length / 3;
  const runIndex = resultMesh.runIndex ?? new Uint32Array([0, triCount * 3]);
  const runOriginalID = resultMesh.runOriginalID ?? new Uint32Array(0);
  const faceID = resultMesh.faceID ?? new Uint32Array(0);
  const result: ResultFaceMeta[] = new Array(triCount);

  let run = 0;
  for (let tri = 0; tri < triCount; tri++) {
    const flatIndex = tri * 3;
    while (
      run + 1 < runOriginalID.length &&
      flatIndex >= (runIndex[run + 1] ?? triCount * 3)
    ) {
      run++;
    }

    const originalID = runOriginalID[run] ?? -1;
    const info = originalInfo.get(originalID);
    const rawFaceID = faceID[tri] ?? -1;
    const ownerFaceIndex =
      info?.side === "owner" && rawFaceID >= 0 && rawFaceID < ownerFaceCount
        ? rawFaceID
        : -1;
    const materialIndex =
      info?.side === "owner" ? Math.max(0, info.materialIndex) : 0;
    const sourceKey = info
      ? `${info.side}:${originalID}:${rawFaceID}:${materialIndex}`
      : `unknown:${tri}`;

    result[tri] = {
      materialIndex,
      ownerFaceIndex,
      sourceKey,
    };
  }

  return result;
}

function clampU16(v: number): number {
  return Math.max(0, Math.min(65535, v | 0));
}

function buildGroupsFromFaceMaterials(
  topology: PolygonTopology,
  materialIndex: Uint16Array,
): GeometryGroup[] {
  const faceCount = topology.offsets.length - 1;
  if (faceCount === 0 || materialIndex.length !== faceCount) return [];

  const groups: GeometryGroup[] = [];
  let runMat = materialIndex[0];
  let triCursor = 0;
  let runStart = 0;

  for (let f = 0; f < faceCount; f++) {
    const size = topology.offsets[f + 1] - topology.offsets[f];
    const faceIndexCount = Math.max(0, size - 2) * 3;
    const mat = materialIndex[f];
    if (mat !== runMat) {
      const count = triCursor - runStart;
      if (count > 0) {
        groups.push({ start: runStart, count, materialIndex: runMat });
      }
      runMat = mat;
      runStart = triCursor;
    }
    triCursor += faceIndexCount;
  }

  const tail = triCursor - runStart;
  if (tail > 0) {
    groups.push({ start: runStart, count: tail, materialIndex: runMat });
  }
  return groups;
}

function materialFaceAttributes(
  materialIndex: Uint16Array,
): FaceAttributeTable | undefined {
  return normalizeFaceAttributes({ materialIndex });
}

function boundsDiagonal(positions: Float32Array): number {
  if (positions.length === 0) return 0;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  return Math.hypot(maxX - minX, maxY - minY, maxZ - minZ);
}

function triangleNormal(
  positions: Float32Array,
  a: number,
  b: number,
  c: number,
): [number, number, number] | null {
  const ax = positions[a * 3];
  const ay = positions[a * 3 + 1];
  const az = positions[a * 3 + 2];
  const bx = positions[b * 3];
  const by = positions[b * 3 + 1];
  const bz = positions[b * 3 + 2];
  const cx = positions[c * 3];
  const cy = positions[c * 3 + 1];
  const cz = positions[c * 3 + 2];
  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const acx = cx - ax;
  const acy = cy - ay;
  const acz = cz - az;
  const nx = aby * acz - abz * acy;
  const ny = abz * acx - abx * acz;
  const nz = abx * acy - aby * acx;
  const len = Math.hypot(nx, ny, nz);
  if (len < 1e-12) return null;
  return [nx / len, ny / len, nz / len];
}

function dotNormalPosition(
  n: readonly [number, number, number],
  positions: Float32Array,
  v: number,
): number {
  return (
    n[0] * positions[v * 3] +
    n[1] * positions[v * 3 + 1] +
    n[2] * positions[v * 3 + 2]
  );
}

function projectedPoint(
  positions: Float32Array,
  v: number,
  dropAxis: 0 | 1 | 2,
): [number, number] {
  const x = positions[v * 3];
  const y = positions[v * 3 + 1];
  const z = positions[v * 3 + 2];
  switch (dropAxis) {
    case 0:
      return [y, z];
    case 1:
      return [x, z];
    case 2:
      return [x, y];
  }
}

function dominantDropAxis(n: readonly [number, number, number]): 0 | 1 | 2 {
  const ax = Math.abs(n[0]);
  const ay = Math.abs(n[1]);
  const az = Math.abs(n[2]);
  return ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
}

function signedProjectedArea(
  positions: Float32Array,
  verts: readonly number[],
  dropAxis: 0 | 1 | 2,
): number {
  let area = 0;
  for (let i = 0; i < verts.length; i++) {
    const [x0, y0] = projectedPoint(positions, verts[i], dropAxis);
    const [x1, y1] = projectedPoint(
      positions,
      verts[(i + 1) % verts.length],
      dropAxis,
    );
    area += x0 * y1 - x1 * y0;
  }
  return area * 0.5;
}

function isConvexProjectedPolygon(
  positions: Float32Array,
  verts: readonly number[],
  dropAxis: 0 | 1 | 2,
  eps: number,
): boolean {
  let sign = 0;
  for (let i = 0; i < verts.length; i++) {
    const [ax, ay] = projectedPoint(
      positions,
      verts[(i + verts.length - 1) % verts.length],
      dropAxis,
    );
    const [bx, by] = projectedPoint(positions, verts[i], dropAxis);
    const [cx, cy] = projectedPoint(
      positions,
      verts[(i + 1) % verts.length],
      dropAxis,
    );
    const cross = (bx - ax) * (cy - by) - (by - ay) * (cx - bx);
    if (Math.abs(cross) <= eps) continue;
    const nextSign = cross > 0 ? 1 : -1;
    if (sign !== 0 && nextSign !== sign) return false;
    sign = nextSign;
  }
  return sign !== 0;
}

function sameResultFaceMeta(a: ResultFaceMeta, b: ResultFaceMeta): boolean {
  return a.materialIndex === b.materialIndex && a.sourceKey === b.sourceKey;
}

function buildTriangleEdgeMap(
  indices: SharedTriangleIndices,
): Map<number, number[]> {
  const edgeToTriangles = new Map<number, number[]>();
  for (let t = 0; t < indices.length / 3; t++) {
    const off = t * 3;
    const verts = [indices[off], indices[off + 1], indices[off + 2]];
    for (let i = 0; i < 3; i++) {
      const key = numEdgeKey(verts[i], verts[(i + 1) % 3]);
      let bucket = edgeToTriangles.get(key);
      if (!bucket) {
        bucket = [];
        edgeToTriangles.set(key, bucket);
      }
      bucket.push(t);
    }
  }
  return edgeToTriangles;
}

function patchBoundaryLoop(
  patch: readonly number[],
  indices: SharedTriangleIndices,
): number[] | null {
  const patchSet = new Set(patch);
  const edgeUse = new Map<number, number>();
  for (const t of patch) {
    const off = t * 3;
    const verts = [indices[off], indices[off + 1], indices[off + 2]];
    for (let i = 0; i < 3; i++) {
      const key = numEdgeKey(verts[i], verts[(i + 1) % 3]);
      edgeUse.set(key, (edgeUse.get(key) ?? 0) + 1);
    }
  }

  const next = new Map<number, number>();
  let start = -1;
  let boundaryEdgeCount = 0;
  for (const t of patchSet) {
    const off = t * 3;
    const verts = [indices[off], indices[off + 1], indices[off + 2]];
    for (let i = 0; i < 3; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % 3];
      if (edgeUse.get(numEdgeKey(a, b)) !== 1) continue;
      if (next.has(a)) return null;
      next.set(a, b);
      if (start < 0) start = a;
      boundaryEdgeCount++;
    }
  }
  if (start < 0 || boundaryEdgeCount < 3) return null;

  const loop: number[] = [];
  let cursor = start;
  for (let guard = 0; guard <= boundaryEdgeCount; guard++) {
    if (loop.includes(cursor)) return null;
    loop.push(cursor);
    const nextVertex = next.get(cursor);
    if (nextVertex === undefined) return null;
    next.delete(cursor);
    cursor = nextVertex;
    if (cursor === start) break;
  }
  if (cursor !== start || next.size !== 0) return null;
  return loop.length === boundaryEdgeCount ? loop : null;
}

function patchIsCoplanarAndConvex(
  positions: Float32Array,
  verts: readonly number[],
  normal: readonly [number, number, number],
  eps: number,
): boolean {
  if (verts.length < 3) return false;
  const planeD = dotNormalPosition(normal, positions, verts[0]);
  for (const v of verts) {
    if (Math.abs(dotNormalPosition(normal, positions, v) - planeD) > eps) {
      return false;
    }
  }
  const dropAxis = dominantDropAxis(normal);
  const area = signedProjectedArea(positions, verts, dropAxis);
  if (Math.abs(area) <= eps * eps) return false;
  return isConvexProjectedPolygon(positions, verts, dropAxis, eps * eps);
}

function polygonizeBooleanTriangles(
  positions: Float32Array,
  indices: SharedTriangleIndices,
  faceMeta: readonly ResultFaceMeta[],
): PolygonizedBooleanTopology {
  const triCount = indices.length / 3;
  if (triCount === 0) {
    const topology = topologyFromTriangles(EMPTY_SHARED_TRIANGLE_INDICES);
    return {
      topology,
      indices: EMPTY_SHARED_TRIANGLE_INDICES,
      sourceFaceForNewFace: new Int32Array(0),
      materialIndex: new Uint16Array(0),
    };
  }

  const diagonal = boundsDiagonal(positions);
  const eps = Math.max(1e-7, diagonal * 1e-6);
  const normalDotMin = 1 - 1e-5;
  const edgeToTriangles = buildTriangleEdgeMap(indices);
  const visited = new Uint8Array(triCount);
  const corners: number[] = [];
  const offsets: number[] = [0];
  const sourceFaceForNewFace: number[] = [];
  const materialIndex: number[] = [];

  const appendFace = (verts: readonly number[], meta: ResultFaceMeta): void => {
    for (const v of verts) corners.push(v);
    offsets.push(corners.length);
    sourceFaceForNewFace.push(meta.ownerFaceIndex);
    materialIndex.push(clampU16(meta.materialIndex));
  };

  for (let seed = 0; seed < triCount; seed++) {
    if (visited[seed]) continue;
    const seedOff = seed * 3;
    const seedNormal = triangleNormal(
      positions,
      indices[seedOff],
      indices[seedOff + 1],
      indices[seedOff + 2],
    );
    if (!seedNormal) {
      visited[seed] = 1;
      appendFace(
        [indices[seedOff], indices[seedOff + 1], indices[seedOff + 2]],
        faceMeta[seed],
      );
      continue;
    }

    const patch: number[] = [];
    const queue = [seed];
    visited[seed] = 1;
    for (let q = 0; q < queue.length; q++) {
      const tri = queue[q];
      patch.push(tri);
      const off = tri * 3;
      const verts = [indices[off], indices[off + 1], indices[off + 2]];
      for (let i = 0; i < 3; i++) {
        const neighbors =
          edgeToTriangles.get(numEdgeKey(verts[i], verts[(i + 1) % 3])) ?? [];
        for (const nextTri of neighbors) {
          if (visited[nextTri]) continue;
          if (!sameResultFaceMeta(faceMeta[seed], faceMeta[nextTri])) continue;
          const noff = nextTri * 3;
          const nNormal = triangleNormal(
            positions,
            indices[noff],
            indices[noff + 1],
            indices[noff + 2],
          );
          if (!nNormal) continue;
          const normalDot =
            seedNormal[0] * nNormal[0] +
            seedNormal[1] * nNormal[1] +
            seedNormal[2] * nNormal[2];
          if (normalDot < normalDotMin) continue;
          const planeD = dotNormalPosition(
            seedNormal,
            positions,
            indices[seedOff],
          );
          let coplanar = true;
          for (let j = 0; j < 3; j++) {
            if (
              Math.abs(
                dotNormalPosition(seedNormal, positions, indices[noff + j]) -
                  planeD,
              ) > eps
            ) {
              coplanar = false;
              break;
            }
          }
          if (!coplanar) continue;
          visited[nextTri] = 1;
          queue.push(nextTri);
        }
      }
    }

    const loop = patch.length > 1 ? patchBoundaryLoop(patch, indices) : null;
    if (loop && patchIsCoplanarAndConvex(positions, loop, seedNormal, eps)) {
      appendFace(loop, faceMeta[seed]);
    } else {
      for (const tri of patch) {
        const off = tri * 3;
        appendFace(
          [indices[off], indices[off + 1], indices[off + 2]],
          faceMeta[tri],
        );
      }
    }
  }

  const offsetsArray = new Uint32Array(offsets);
  const cornersArray = new Uint32Array(corners);
  const edgeData = buildEdgesAndCornerEdges(offsetsArray, cornersArray);
  const topology: PolygonTopology = {
    offsets: offsetsArray,
    corners: cornersArray,
    edges: edgeData.edges,
    cornerEdges: edgeData.cornerEdges,
    edgeFlags: edgeData.edgeFlags,
  };

  return {
    topology,
    indices: triangulateTopology(topology),
    sourceFaceForNewFace: new Int32Array(sourceFaceForNewFace),
    materialIndex: new Uint16Array(materialIndex),
  };
}

/**
 * Evaluate a Boolean CSG modifier.
 *
 * Falls through to the input geometry when:
 *   - manifold-3d is not ready yet (WASM still loading),
 *   - the cutter reference is unset or resolves to empty geometry,
 *   - either side fails to construct a valid Manifold (non-manifold input),
 *   - the operation throws.
 */
export function booleanCore(
  geo: ModifierGeometry,
  mod: BooleanMod,
  input?: ModifierEvaluationInput,
): ModifierGeometry {
  if (!mod.targetObjectName) return geo;
  const cutter = input?.sourceGeometries?.[mod.targetObjectName];
  if (!cutter || cutter.positions.length === 0 || cutter.indices.length === 0) {
    return geo;
  }
  if (geo.positions.length === 0 || geo.indices.length === 0) return geo;

  const toplevel = getManifold();
  if (!toplevel) {
    // Manifold WASM still initialising — pass through on this frame; the
    // next re-evaluation (after ensureManifoldReady resolves) will run the
    // op. Mirrors `applyCoreMod`'s `isWasmReady()` guard for subsurf.
    return geo;
  }

  const ManifoldCtor = toplevel.Manifold;
  const MeshCtor = toplevel.Mesh;

  let ownerMesh: Mesh | null = null;
  let cutterMesh: Mesh | null = null;
  let ownerManifold: Manifold | null = null;
  let cutterManifold: Manifold | null = null;
  let resultManifold: Manifold | null = null;
  let resultMesh: Mesh | null = null;

  try {
    const tolerance = Math.max(0, mod.holeTolerance);
    const ownerBuilt = buildManifoldMesh(
      ManifoldCtor,
      MeshCtor,
      geo,
      "owner",
      tolerance,
    );
    const cutterBuilt = buildManifoldMesh(
      ManifoldCtor,
      MeshCtor,
      cutter,
      "cutter",
      tolerance,
    );
    const originalInfo = combineOriginalInfo(
      ownerBuilt.originalInfo,
      cutterBuilt.originalInfo,
    );
    ownerMesh = ownerBuilt.mesh;
    cutterMesh = cutterBuilt.mesh;

    ownerManifold = new ManifoldCtor(ownerMesh);
    if (ownerManifold.status() !== "NoError" || ownerManifold.isEmpty()) {
      return geo;
    }
    cutterManifold = new ManifoldCtor(cutterMesh);
    if (cutterManifold.status() !== "NoError" || cutterManifold.isEmpty()) {
      return geo;
    }

    switch (mod.operation) {
      case "union":
        resultManifold = ownerManifold.add(cutterManifold);
        break;
      case "difference":
        resultManifold = ownerManifold.subtract(cutterManifold);
        break;
      case "intersect":
        resultManifold = ownerManifold.intersect(cutterManifold);
        break;
    }

    if (!resultManifold || resultManifold.isEmpty()) {
      // Empty CSG result (e.g. intersect with non-overlapping cutter) —
      // return a trivially empty mesh so downstream stages see zero faces
      // rather than the owner's unchanged geometry, which would silently
      // defeat a Difference that fully consumes the owner.
      const topology = topologyFromTriangles(EMPTY_SHARED_TRIANGLE_INDICES);
      return {
        positions: new Float32Array(0),
        indices: EMPTY_SHARED_TRIANGLE_INDICES,
        normals: null,
        uvs: null,
        morphArrays: [],
        skinIndex: null,
        skinWeight: null,
        sculptDisplacement: null,
        groups: [],
        edges: null,
        edgeFlags: null,
        edgeCreases: null,
        skinRadii: null,
        topology,
        faceAttributes: undefined,
        faceProvenance: faceProvenanceFromSourceMap(
          ownerBuilt.faceCount,
          new Int32Array(0),
        ),
        provenance: identityProvenance(0),
      };
    }

    resultMesh = resultManifold.getMesh();

    // Extract positions: Manifold's Mesh stores interleaved x,y,z,(extra props)
    // per vertex. numProp is 3 in our case, so vertProperties is exactly the
    // flat position buffer we want.
    const numProp = resultMesh.numProp;
    const resultVertCount = resultMesh.numVert;
    const outPositions = new Float32Array(resultVertCount * 3);
    const src = resultMesh.vertProperties;
    if (numProp === 3) {
      outPositions.set(src);
    } else {
      // Defensive path: strip extra props if Manifold ever returns numProp > 3.
      for (let v = 0; v < resultVertCount; v++) {
        outPositions[v * 3] = src[v * numProp];
        outPositions[v * 3 + 1] = src[v * numProp + 1];
        outPositions[v * 3 + 2] = src[v * numProp + 2];
      }
    }
    // triVerts is already a flat Uint32Array of triangle indices.
    const outIndices = asSharedTriangleIndices(
      new Uint32Array(resultMesh.triVerts),
    );
    const faceMeta = resultTriangleMetadata(
      resultMesh,
      originalInfo,
      ownerBuilt.faceCount,
    );
    const polygonized = polygonizeBooleanTriangles(
      outPositions,
      outIndices,
      faceMeta,
    );
    const topology = polygonized.topology;
    const faceProvenance = faceProvenanceFromSourceMap(
      ownerBuilt.faceCount,
      polygonized.sourceFaceForNewFace,
    );
    const faceAttributes = materialFaceAttributes(polygonized.materialIndex);
    const groups = buildGroupsFromFaceMaterials(
      topology,
      polygonized.materialIndex,
    );

    // Provenance: for each output vertex, find the nearest OWNER vertex in
    // the pre-op positions. Vertices that came from the cutter (difference
    // side, intersection interior) will land far from every owner vertex —
    // we accept that imprecision in v1: the reconcile layer averages parent
    // attributes, and a nearest-vertex fallback still produces usable
    // vertex-group / skin-radii / sculpt inheritance on the unchanged parts
    // of the owner, which is the common case users care about.
    const ownerVertCount = geo.positions.length / 3;
    const grid = buildVertexGrid(geo.positions);
    const srcForNew = new Int32Array(resultVertCount);
    for (let v = 0; v < resultVertCount; v++) {
      srcForNew[v] = nearestOwnerVertex(
        grid,
        geo.positions,
        outPositions[v * 3],
        outPositions[v * 3 + 1],
        outPositions[v * 3 + 2],
      );
    }
    const provenance = provenanceFromSourceMap(ownerVertCount, srcForNew);

    const outGeo: ModifierGeometry = {
      positions: outPositions,
      indices: polygonized.indices,
      // CSG invalidates everything derived from the pre-op topology.
      normals: null,
      uvs: null,
      morphArrays: [],
      skinIndex: null,
      skinWeight: null,
      sculptDisplacement: null,
      groups,
      morphTargetsRelative: geo.morphTargetsRelative,
      morphNames: geo.morphNames,
      // Wire edges, edge flags, and skin radii are all tied to the pre-op
      // topology and cannot be propagated through a retopologising CSG op.
      edges: topology.edges.length > 0 ? topology.edges : null,
      edgeFlags: topology.edgeFlags.length > 0 ? topology.edgeFlags : null,
      edgeCreases: topology.edgeCreases ?? null,
      skinRadii: null,
      topology,
      faceAttributes,
      faceProvenance,
      provenance,
    };

    return outGeo;
  } catch (err) {
    // Any failure in the CSG kernel — non-manifold input, degenerate geometry,
    // or an internal assertion — degrades gracefully to pass-through so the
    // user sees the pre-op geometry instead of a crashed viewport.
    console.warn(
      "booleanCore: CSG evaluation failed, passing through owner geometry",
      err,
    );
    return geo;
  } finally {
    // Manifold objects live in WASM memory and must be freed manually.
    // Mesh is a plain JS wrapper — no explicit free is needed, so we only
    // release the Manifold handles. The typed-array views (vertProperties,
    // triVerts) are garbage-collected normally with the Mesh object.
    resultManifold?.delete();
    cutterManifold?.delete();
    ownerManifold?.delete();
    // Silence unused-variable diagnostics for the mesh handles — they are
    // kept alive through the try block so Manifold construction sees the
    // buffers, then released along with this stack frame.
    void ownerMesh;
    void cutterMesh;
    void resultMesh;
  }
}
